import React from 'react';
import Module from 'Components/Core/Module';
import useBrokerageConnections from 'Components/Banking/useBrokerageConnections';
import BrokerageLoginForm from 'Components/Banking/BrokerageLoginForm';
import BrokerageOrderForm from 'Components/Banking/BrokerageOrderForm';
import syncBrokerageNow from 'Components/Banking/syncBrokerageNow';
import { registerBrokerageDataListener } from 'Components/Banking/brokerageDataBus';
import './Brokerage.css';

// Rueckbau 2026-07-25: Depot+Orderbuch werden nur noch aus dem lokalen Spiegel
// (bankingBrokerageDepots/-Positions) angezeigt, OHNE jede comdirect-
// Verbindung - die wird nur bei einem expliziten "Jetzt synchronisieren"-
// Klick oder einer Order-Platzierung geoeffnet. Seit dem Umbau vom
// 2026-07-26 (Nutzervorgabe) bleibt sie danach bestehen (nicht mehr sofort
// widerrufen), bis sie explizit ueber den Logout in der Top-Navigation
// geschlossen wird, eine andere Verbindung sie ersetzt, oder sie nach
// comdirects ~10 Minuten Token-Gueltigkeit von selbst verfaellt (siehe
// BrokerageTask::syncBrokerageData()). Kein Login-Gate fuer die reine
// Ansicht noetig.
//
// Muss (wie ModuleTable es intern tut) in <Module> gewrappt werden - nur das
// registriert bei x.wam[id].show()/hide() die tatsaechliche Ein-/Ausblende-
// Logik beim Seitenwechsel. Ohne diesen Wrapper bleibt die Seite dauerhaft im
// DOM sichtbar und ueberlappt beim Navigieren mit anderen Modulen.
export default function BrokerageOverview(props) {

	const x = props.x;
	const connections = useBrokerageConnections(x);

	const [connectionId, setConnectionId] = React.useState(null);
	const [depots, setDepots] = React.useState(null);
	const [lastSyncAt, setLastSyncAt] = React.useState(null);
	const [error, setError] = React.useState('');
	// null = unsortiert (Reihenfolge der Bank). Klick auf dieselbe Spalte dreht
	// nur die Richtung um, Klick auf eine andere Spalte setzt neu auf aufsteigend.
	const [sortKey, setSortKey] = React.useState(null);
	const [sortDir, setSortDir] = React.useState(1);
	const [orderTarget, setOrderTarget] = React.useState(null);
	const [showSync, setShowSync] = React.useState(false);
	// Fortschrittsanzeige fuer das Reload-Icon (Chat vom 2026-07-26) - nur fuer
	// den schnellen Weg ueber eine bereits offene Verbindung (syncBrokerageNow);
	// braucht sie einen neuen Login, uebernimmt der Login-Dialog selbst die
	// Fortschrittsanzeige (siehe BrokerageLoginForm.js).
	const [syncing, setSyncing] = React.useState(false);

	function toggleSort(key) {
		if (sortKey === key) {
			setSortDir((current) => -current);
		} else {
			setSortKey(key);
			setSortDir(1);
		}
	}

	function sortArrow(key) {
		if (sortKey !== key) return null;
		return <i className={`bi ${sortDir === 1 ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} brokerage-sort-icon`}></i>;
	}

	function positionName(position) {
		return position.name
			|| (position.instrument && (position.instrument.name || position.instrument.wkn))
			|| position.wkn || position.instrumentId || 'Position';
	}

	// Rohen Stückzahl-Wert extrahieren - "position.quantity" ist entweder ein
	// {value, unit}-Objekt oder direkt eine Zahl. BUGFIX (2026-07-31, live
	// beobachtet: "NaN" bei Positionen mit Stückzahl 0, das dann per
	// Summenbildung sogar die GESAMTE Depotsumme auf "NaN" kippte): die
	// vorherige Fassung nutzte "a || b || c" zur Extraktion - bei einer
	// echten Zahl 0 ist "position.quantity.value" selbst falsy, wodurch die
	// Kette faelschlich auf "position.quantity" (das ganze Objekt statt der
	// Zahl) zurueckfiel. parseFloat(Objekt) ergibt NaN. Explizite
	// Typprüfung statt "||"-Verkettung vermeidet diese Falle.
	function rawQuantity(position) {
		if (position.quantity && typeof position.quantity === 'object') {
			return position.quantity.value;
		}
		return position.quantity;
	}

	function formatQuantity(position) {
		const value = rawQuantity(position);
		return (value === '' || value === null || value === undefined) ? '' : String(Math.round(parseFloat(value)));
	}

	// Werte kommen als decimal(18,4)-String aus der DB (z.B. "13821.912") -
	// auf 2 Nachkommastellen fuer die Anzeige runden (Nutzerwunsch). NaN
	// (z.B. bei kaputten/fehlenden Rohdaten) bewusst wie "kein Wert"
	// behandelt statt eine "NaN"-Zeichenkette anzuzeigen.
	function formatValue(value) {
		if (value === undefined || value === null || value === '') return '–';
		const parsed = parseFloat(value);
		return Number.isNaN(parsed) ? '–' : parsed.toFixed(2);
	}

	// Anschaffungskurs in der Waehrung, in der der Kauf tatsaechlich
	// ausgefuehrt wurde (z.B. AUD bei einem Kauf in Australien) - comdirect
	// liefert dafuer nur einen EUR-Wert, der Backend rechnet ihn mit dem
	// AKTUELLEN Wechselkurs um (siehe BrokerageLocalDepotList::attachPrices()).
	// Drei statt zwei Nachkommastellen (Nutzerwunsch, Chat vom 2026-08-04),
	// da die meisten dieser Positionen sehr kleine Kurse haben (Cent-Bereich),
	// bei denen zwei Nachkommastellen zu grob runden.
	function formatPurchasePrice(position) {
		const purchasePrice = position.purchasePrice;
		if (!purchasePrice || purchasePrice.value === undefined || purchasePrice.value === null) return '–';
		const parsed = parseFloat(purchasePrice.value);
		if (Number.isNaN(parsed)) return '–';
		return parsed.toFixed(3) + (purchasePrice.unit ? ' ' + purchasePrice.unit : '');
	}

	function positionQuantity(position) {
		const value = rawQuantity(position);
		if (value === '' || value === null || value === undefined) return 0;
		const parsed = parseFloat(value);
		return Number.isNaN(parsed) ? 0 : parsed;
	}

	// comdirects eigener Positionswert (Gesamtwert, nicht Stueckkurs) - nur als
	// Fallback genutzt, siehe positionPrice().
	function comdirectValue(position) {
		const raw = position.currentValue && position.currentValue.value;
		if (raw === undefined || raw === null || raw === '') return null;
		const parsed = parseFloat(raw);
		return Number.isNaN(parsed) ? null : parsed;
	}

	// Kurs kommt seit 2026-07-31 primaer ueber EODHD/IG (Marketdata-Modul)
	// statt von comdirect - "priceEur" ist bereits EUR-umgerechnet (siehe
	// BrokerageLocalDepotList.inc::attachPrices()), damit Positionen in
	// unterschiedlichen Waehrungen (USD/CAD/AUD/...) ueberhaupt summierbar
	// bleiben. Fallback (Nutzervorgabe 2026-07-31): fuer Positionen, die bei
	// KEINEM Marktdatenanbieter gelistet sind (typischerweise bankeigene
	// Optionsscheine/Zertifikate wie "J.P. Morgan SE Put..." - siehe
	// BrokerageTask::syncDepotWatchlist()), wird stattdessen comdirects
	// eigener Positionswert genutzt, umgerechnet auf einen impliziten
	// Stueckkurs (Wert / Stueckzahl) - damit bleibt "Wert = Kurs x Stückzahl"
	// (siehe positionValue()) auch fuer diese Positionen korrekt. Erst wenn
	// auch das fehlt, bleibt es endgueltig "–".
	function positionPrice(position) {
		if (position.priceEur !== undefined && position.priceEur !== null) {
			const parsed = parseFloat(position.priceEur);
			if (!Number.isNaN(parsed)) return parsed;
		}
		const value = comdirectValue(position);
		const quantity = positionQuantity(position);
		return (value !== null && quantity > 0) ? value / quantity : null;
	}

	// true, wenn positionPrice() auf den comdirect-Fallback zurueckgegriffen
	// hat (fuer die "von comdirect übernommen"-Kennzeichnung in der UI).
	function positionPriceFromComdirect(position) {
		if (position.priceEur !== undefined && position.priceEur !== null && !Number.isNaN(parseFloat(position.priceEur))) {
			return false;
		}
		return comdirectValue(position) !== null && positionQuantity(position) > 0;
	}

	// "Wert" wird jetzt IMMER aus Kurs x Stueckzahl berechnet (Nutzervorgabe),
	// nicht mehr aus comdirects currentValue - null, wenn kein Kurs vorliegt
	// (siehe positionPrice()), damit diese Position bewusst aus der Depot-
	// Summe herausfaellt statt sie stillschweigend zu verfaelschen.
	function positionValue(position) {
		const price = positionPrice(position);
		return price === null ? null : price * positionQuantity(position);
	}

	function depotTotal(positions) {
		return (positions || []).reduce((sum, position) => {
			const value = positionValue(position);
			return value === null ? sum : sum + value;
		}, 0);
	}

	function sortedPositions(positions) {
		if (sortKey === null) return positions;
		const extract = sortKey === 'name' ? positionName : (sortKey === 'quantity' ? positionQuantity : positionValue);
		return [...positions].sort((a, b) => {
			const valueA = extract(a);
			const valueB = extract(b);
			return (typeof valueA === 'string' ? valueA.localeCompare(valueB) : valueA - valueB) * sortDir;
		});
	}

	// Erste Verbindung automatisch vorauswaehlen, statt eine unnoetige Auswahl
	// zu erzwingen - umschalten auf eine andere geht danach jederzeit ueber den
	// Verbindungs-Wechsler (switchConnection()).
	React.useEffect(() => {
		if (connectionId === null && connections && connections.length > 0) {
			setConnectionId(connections[0].connectionid);
		}
	}, [connections, connectionId]);

	function switchConnection(id) {
		if (id === connectionId) return;
		setDepots(null);
		setError('');
		setConnectionId(id);
	}

	function loadDepots() {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageLocalDepotList', { connectionId }, (result) => {
			const content = result.data.content || {};
			// Debug (Chat vom 2026-07-26): zeigt die von BrokerageLocalDepotList
			// gelieferten (bereits auf unser Schema reduzierten) Depot/Positions-
			// Daten in der Konsole.
			console.log('BrokerageLocalDepotList:', content);
			if (result.data.returncode !== 1) {
				setError(content.message || 'Depot konnte nicht geladen werden.');
				return;
			}
			setError('');
			setDepots(content.depots || []);
			setLastSyncAt(content.lastSyncAt || null);
		});
	}

	React.useEffect(() => {
		if (connectionId !== null) loadDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	// Auf Synchronisierungen von ANDEREN Brokerage-Seiten reagieren (Chat vom
	// 2026-07-26, Bugreport: Depot zeigte eine frisch ausgefuehrte Order noch
	// nicht, bis hier zusaetzlich separat auf Reload geklickt wurde) - ueber
	// ein Ref, damit der Listener immer die aktuelle loadDepots()/connectionId
	// verwendet, obwohl er nur einmal registriert wird.
	const loadDepotsRef = React.useRef(loadDepots);
	loadDepotsRef.current = loadDepots;
	React.useEffect(() => {
		return registerBrokerageDataListener(x, () => {
			if (connectionId !== null) loadDepotsRef.current();
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	function openOrder(depot, position, side) {
		setOrderTarget({
			depotId: depot.depotId,
			depotLabel: depot.depotDisplayId,
			settlementAccountId: depot.settlementAccountId,
			side,
			instrumentId: position.instrumentId || position.wkn || '',
			instrumentLabel: positionName(position),
			instrumentIsin: position.isin || null,
			custodyCountry: position.venueCountry || null,
			// Immer mit der gehaltenen Stueckzahl vorbelegen (Nutzerwunsch), bleibt
			// natuerlich editierbar - fuer Kauf wie Verkauf gleichermassen.
			quantity: formatQuantity(position),
		});
	}

	function closeOrder() {
		setOrderTarget(null);
		loadDepots();
	}

	const connection = connections ? connections.find((c) => c.connectionid === connectionId) : null;

	let inner;
	if (connections === null) {
		inner = <p>Lade…</p>;
	} else if (connections.length === 0) {
		inner = <p>Keine comdirect-Verbindung vorhanden. Bitte zuerst unter Banking → Konten eine comdirect-Verbindung anlegen.</p>;
	} else if (connectionId === null) {
		inner = (
			<div className="brokerage-picker">
				{connections.map((c) => (
					<div key={c.connectionid} className="brokerage-picker-item" onClick={() => setConnectionId(c.connectionid)}>
						{c.bankname} – {c.accountlabel}
					</div>
				))}
			</div>
		);
	} else {
		inner = (
			<React.Fragment>
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				{depots === null && <p>Lade Depot…</p>}
				{depots !== null && depots.length === 0 && <p>Noch keine Daten - bitte einmal synchronisieren.</p>}
				{(depots || []).map((depot) => {
					const totalValue = depotTotal(depot.positions);
					return (
						<div key={depot.depotId} className="brokerage-depot">
							{(depot.positions || []).length === 0 &&
								<React.Fragment>
									<div className="brokerage-depot-header">
										<div>
											<strong>Depot {depot.depotDisplayId}</strong>
											{connection && <span className="brokerage-hint"> – Inhaber: {connection.accountlabel}</span>}
										</div>
										<strong>{formatValue(totalValue)} EUR</strong>
									</div>
									<p className="brokerage-hint">Keine Positionen.</p>
								</React.Fragment>
							}
							{(depot.positions || []).length > 0 &&
								<React.Fragment>
									{/* Depot-Kopf + Spaltenkopf zusammen "sticky" (Nutzervorgabe:
									    nur die Positionszeilen sollen scrollen, alles andere bleibt
									    fix) - beide zusammen in EINEM sticky-Element, damit ihre
									    kombinierte (variable) Hoehe nicht von Hand berechnet werden
									    muss (siehe .brokerage-depot-sticky-header in Brokerage.css).
									    BEWUSST ausserhalb von .brokerage-table-scroll: dessen
									    overflow-x:auto wuerde laut CSS-Spec automatisch auch
									    overflow-y auf "auto" erzwingen (ein explizites
									    "overflow-y:visible" wird dabei ignoriert), wodurch das
									    Element selbst zum naechsten Scroll-Vorfahren fuer "sticky"
									    wuerde statt ".brokerage-scroll-area" - der Header waere dann
									    nicht mehr wirklich fix, sondern wuerde nur INNERHALB seines
									    eigenen (nie tatsaechlich ueberlaufenden) Kastens "kleben" und
									    trotzdem mit der gesamten Karte wegscrollen (genau der Bug,
									    der hier beobachtet wurde). */}
									<div className="brokerage-depot-sticky-header">
										<div className="brokerage-depot-header">
											<div>
												<strong>Depot {depot.depotDisplayId}</strong>
												{connection && <span className="brokerage-hint"> – Inhaber: {connection.accountlabel}</span>}
											</div>
											<strong>{formatValue(totalValue)} EUR</strong>
										</div>
										<div className="brokerage-position brokerage-position-header">
											<span className="brokerage-sortable" onClick={() => toggleSort('name')}>Wertpapier{sortArrow('name')}</span>
											<span>WKN</span>
											<span className="brokerage-sortable" onClick={() => toggleSort('quantity')}>Stückzahl{sortArrow('quantity')}</span>
											<span>Kaufkurs</span>
											<span>Kurs (EUR)</span>
											<span className="brokerage-sortable" onClick={() => toggleSort('value')}>Wert (EUR){sortArrow('value')}</span>
											<span></span>
										</div>
									</div>
									<div className="brokerage-table-scroll">
									{sortedPositions(depot.positions || []).map((position, idx) => (
										<div
											key={position.positionId || idx}
											className="brokerage-position brokerage-position-clickable"
											onClick={() => openOrder(depot, position, 'SELL')}
										>
											<span>{positionName(position)}</span>
											<span>{position.wkn || ''}{position.venueCountry ? ` (${position.venueCountry})` : ''}</span>
											<span>{formatQuantity(position)}</span>
											<span>{formatPurchasePrice(position)}</span>
											<span title={positionPriceFromComdirect(position) ? 'Kurs von comdirect übernommen (bei EODHD/IG nicht gelistet)' : undefined}>
											{formatValue(positionPrice(position))}
										</span>
											<span>{formatValue(positionValue(position))}</span>
											<span className="brokerage-position-actions">
												{position.isin &&
													<img
														src="/comdirect-favicon.ico"
														alt="comdirect"
														className="brokerage-action-icon brokerage-action-external"
														title="Bei comdirect ansehen"
														onClick={(e) => { e.stopPropagation(); window.open('https://www.comdirect.de/inf/aktien/' + encodeURIComponent(position.isin), '_blank', 'noopener,noreferrer'); }}
													/>
												}
												<i
													className="bi bi-plus-circle brokerage-action-icon brokerage-action-buy"
													title="Kaufen"
													onClick={(e) => { e.stopPropagation(); openOrder(depot, position, 'BUY'); }}
												></i>
												<i
													className="bi bi-dash-circle brokerage-action-icon brokerage-action-sell"
													title="Verkaufen"
													onClick={(e) => { e.stopPropagation(); openOrder(depot, position, 'SELL'); }}
												></i>
											</span>
										</div>
									))}
								</div>
								</React.Fragment>
							}
						</div>
					);
				})}
			</React.Fragment>
		);
	}

	return (
		<Module id={props.id} x={x} classes="module-brokerage-overview" title={props.title} display="d-block">
			{/* Flex-Spalte ueber die volle Modulhoehe (siehe
			    ".module-brokerage-overview .module-content" in Brokerage.css,
			    ersetzt den sonst appweiten Auto-Scroll auf Modulebene) -
			    Toolbar bleibt als shrink-0-Element fix, nur ".brokerage-scroll-area"
			    darunter scrollt (Nutzervorgabe: nur die Positionszeilen sollen
			    scrollen, der Rest bleibt fix). */}
			<div className="brokerage-page">
			<div className="brokerage-toolbar">
				{connections && connections.length > 1 &&
					<div className="brokerage-connection-switch">
						<label htmlFor="brokerage-connection-select">Verbindung</label>
						<select
							id="brokerage-connection-select"
							value={connectionId || ''}
							onChange={(e) => switchConnection(parseInt(e.target.value, 10))}
						>
							{connections.map((c) => (
								<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
							))}
						</select>
					</div>
				}
				{connectionId !== null &&
					<i
						className={`bi bi-arrow-repeat brokerage-action-icon brokerage-toolbar-reload ${syncing ? 'brokerage-icon-spin' : ''}`}
						title="Jetzt synchronisieren"
						onClick={() => {
							if (syncing) return;
							setSyncing(true);
							setError('');
							syncBrokerageNow(x, connectionId, {
								onDone: () => { setSyncing(false); loadDepots(); },
								onNeedsLogin: () => { setSyncing(false); setShowSync(true); },
								onError: (message) => { setSyncing(false); setError(message); },
							});
						}}
					></i>
				}
				{syncing && <span className="brokerage-hint">Synchronisiere…</span>}
				{!syncing && lastSyncAt && <span className="brokerage-hint">Letzter Sync: {lastSyncAt}</span>}
			</div>
			<div className="brokerage-scroll-area">
				{inner}
			</div>
			</div>
			{showSync &&
				<div className="brokerage-overlay">
					<BrokerageLoginForm
						x={x}
						connectionId={connectionId}
						accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
						pinUse={true}
						pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
						purpose="sync"
						onLoggedIn={() => { setShowSync(false); loadDepots(); }}
						onCancel={() => setShowSync(false)}
					/>
				</div>
			}
			{orderTarget &&
				<div className="brokerage-overlay">
					<BrokerageOrderForm
						x={x}
						connectionId={connectionId}
						accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
						pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
						instrumentLabel={orderTarget.instrumentLabel}
						instrumentIsin={orderTarget.instrumentIsin}
						custodyCountry={orderTarget.custodyCountry}
						depotLabel={orderTarget.depotLabel}
						initialOrder={{
							depotId: orderTarget.depotId,
							settlementAccountId: orderTarget.settlementAccountId,
							instrumentId: orderTarget.instrumentId,
							venueId: '',
							side: orderTarget.side,
							quantity: orderTarget.quantity,
							limitPrice: '',
						}}
						onCancel={closeOrder}
						onDone={closeOrder}
					/>
				</div>
			}
		</Module>
	)

};
